import FetchClient, { type ApiResponse } from "./FetchClient";

export interface PublicPreviewDeal {
  name: string;
  image: string;
  retailer: string;
  discountBand: "25%+ off" | "50%+ off" | "70%+ off";
  proximityLabel: "Nearby";
}

export interface PublicDealPreview {
  zipCode: string;
  deals: PublicPreviewDeal[];
  storesScanned: number;
  savingsSignal:
    | "No qualifying savings found"
    | "Savings found nearby"
    | "Strong savings nearby"
    | "Exceptional savings nearby";
  generatedAt: number;
}

const DISCOUNT_BANDS = new Set<PublicPreviewDeal["discountBand"]>([
  "25%+ off",
  "50%+ off",
  "70%+ off",
]);
const SAVINGS_SIGNALS = new Set<PublicDealPreview["savingsSignal"]>([
  "No qualifying savings found",
  "Savings found nearby",
  "Strong savings nearby",
  "Exceptional savings nearby",
]);
const FORBIDDEN_PREVIEW_FIELDS = new Set([
  "id",
  "dealId",
  "upc",
  "sku",
  "storeId",
  "storeAddress",
  "price",
  "storePrice",
  "localPrice",
  "referencePrice",
  "onlinePrice",
  "msrp",
  "savings",
  "potentialSavings",
  "discount",
  "discountPercentage",
  "distance",
  "inventory",
  "quantity",
  "stock",
  "inStock",
  "score",
  "opportunityScore",
  "aisle",
  "latitude",
  "longitude",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function containsForbiddenPreviewField(value: Record<string, unknown>): boolean {
  return Object.keys(value).some((key) => FORBIDDEN_PREVIEW_FIELDS.has(key));
}

function parsePreviewDeal(value: unknown): PublicPreviewDeal | undefined {
  if (!isRecord(value) || containsForbiddenPreviewField(value)) return undefined;
  const { name, image, retailer, discountBand, proximityLabel } = value;
  if (
    typeof name !== "string" ||
    !name.trim() ||
    name.length > 500 ||
    typeof image !== "string" ||
    image.length > 2_000 ||
    typeof retailer !== "string" ||
    !retailer.trim() ||
    retailer.length > 64 ||
    typeof discountBand !== "string" ||
    !DISCOUNT_BANDS.has(discountBand as PublicPreviewDeal["discountBand"]) ||
    proximityLabel !== "Nearby"
  ) {
    return undefined;
  }
  return {
    name: name.trim(),
    image,
    retailer: retailer.trim(),
    discountBand: discountBand as PublicPreviewDeal["discountBand"],
    proximityLabel,
  };
}

/**
 * Treat the public preview response as untrusted. This both prevents an older
 * exact-data payload from being rendered and fails visibly if production drifts
 * away from the coarse public contract.
 */
export function parsePublicDealPreview(value: unknown): PublicDealPreview {
  if (!isRecord(value) || containsForbiddenPreviewField(value)) {
    throw new Error("Local preview is temporarily unavailable.");
  }
  const deals = Array.isArray(value.deals)
    ? value.deals.map(parsePreviewDeal)
    : [];
  if (
    !Array.isArray(value.deals) ||
    deals.some((deal) => !deal) ||
    typeof value.zipCode !== "string" ||
    !/^\d{5}$/.test(value.zipCode) ||
    typeof value.storesScanned !== "number" ||
    !Number.isSafeInteger(value.storesScanned) ||
    value.storesScanned < 0 ||
    typeof value.savingsSignal !== "string" ||
    !SAVINGS_SIGNALS.has(value.savingsSignal as PublicDealPreview["savingsSignal"]) ||
    typeof value.generatedAt !== "number" ||
    !Number.isSafeInteger(value.generatedAt) ||
    value.generatedAt <= 0
  ) {
    throw new Error("Local preview is temporarily unavailable.");
  }
  return {
    zipCode: value.zipCode,
    deals: deals as PublicPreviewDeal[],
    storesScanned: value.storesScanned,
    savingsSignal: value.savingsSignal as PublicDealPreview["savingsSignal"],
    generatedAt: value.generatedAt,
  };
}

/** In-store clearance teaser for a ZIP; `retailer` is the chain id (walmart, target, bestbuy, costco, home_depot). */
export async function getPublicDealPreview(zipCode: string, retailer = "walmart"): Promise<PublicDealPreview> {
  const params = new URLSearchParams({ zipCode });
  if (retailer !== "walmart") params.set("retailer", retailer);
  const response = await FetchClient.get<ApiResponse<PublicDealPreview>>(
    `/public/deal-preview?${params.toString()}`,
    {},
    "optional",
  );
  const preview = parsePublicDealPreview(response.data);
  // Only this chain's finds: an API without per-chain previews answers with
  // Walmart items, which must never show under another store's title.
  return { ...preview, deals: preview.deals.filter((deal) => deal.retailer === retailer) };
}

/** ZIP of the nearest store to a browser location, for filling in "clearance near you". Null when nothing is close. */
export async function getZipFromCoordinates(lat: number, lng: number): Promise<string | null> {
  const params = new URLSearchParams({ lat: lat.toFixed(4), lng: lng.toFixed(4) });
  try {
    const response = await FetchClient.get<ApiResponse<{ zipCode?: string }>>(`/public/zip-from-coords?${params.toString()}`, {}, "optional");
    const zip = response.data?.zipCode || "";
    return /^\d{5}$/.test(zip) ? zip : null;
  } catch {
    return null;
  }
}
